import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, ArrowRight, TrendingUp, Circle, Printer, FileImage, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtL = (v) => {
  if (!v && v !== 0) return "0";
  const l = v / 100000;
  if (Math.abs(l) >= 100) return `${Math.round(l).toLocaleString("en-IN")} L`;
  if (Math.abs(l) >= 10) return `${l.toFixed(1)} L`;
  return `${l.toFixed(2)} L`;
};

export default function ClosingReport({ report, open, onClose }) {
  const captureRef = useRef(null);
  if (!report) return null;

  const { week_start, week_end, next_week_start, totals, counts, entries } = report;
  const collectionPct = totals.gap_vs_planned_pct || 0;
  const fileStem = `closing_${week_start}_to_${week_end}`;

  const printReport = () => window.print();

  const downloadPNG = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const el = captureRef.current;
      if (!el) return;
      const canvas = await html2canvas(el, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElement("a");
      link.download = `${fileStem}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Snapshot downloaded");
    } catch {
      toast.error("PNG snapshot failed");
    }
  };

  const downloadPDF = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const jsPDFmod = await import("jspdf");
      const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default;
      const el = captureRef.current;
      if (!el) return;
      const canvas = await html2canvas(el, { backgroundColor: "#ffffff", scale: 2 });
      const img = canvas.toDataURL("image/png");
      // Fit to A4 portrait
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let w = pageW - 20;
      let h = w / ratio;
      if (h > pageH - 20) { h = pageH - 20; w = h * ratio; }
      pdf.addImage(img, "PNG", (pageW - w) / 2, 10, w, h);
      pdf.save(`${fileStem}.pdf`);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[860px] max-h-[92vh] overflow-y-auto" data-testid="closing-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Weekly Closing Report
            </span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={downloadPNG} className="gap-1 text-slate-500 hover:text-slate-700" data-testid="download-png-btn" title="Download PNG snapshot">
                <FileImage className="w-3.5 h-3.5" /> PNG
              </Button>
              <Button variant="ghost" size="sm" onClick={downloadPDF} className="gap-1 text-slate-500 hover:text-slate-700" data-testid="download-pdf-btn" title="Download PDF">
                <FileText className="w-3.5 h-3.5" /> PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={printReport} className="gap-1 text-slate-500 hover:text-slate-700" data-testid="print-closing-report-btn">
                <Printer className="w-3.5 h-3.5" /> Print
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2" ref={captureRef}>
          {/* Header dates */}
          <div className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-white p-3 rounded-md border">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Closed Week</p>
              <p className="text-sm font-bold text-slate-800">
                {new Date(week_start + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                {" – "}
                {new Date(week_end + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Next Week Starts</p>
              <p className="text-sm font-bold text-[#0066CC]">
                {new Date(next_week_start + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card><CardContent className="p-3">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Planned</p>
              <p className="text-xl font-bold text-slate-800 mt-1">₹{fmtL(totals.planned)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{counts.total_entries} entries</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Collected</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">₹{fmtL(totals.collected)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{collectionPct}% of planned</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Carried Forward</p>
              <p className="text-xl font-bold text-amber-600 mt-1">₹{fmtL(totals.carried)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{counts.carried_forward} items → next week</p>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Fully Received</p>
              <p className="text-xl font-bold text-[#0066CC] mt-1">{counts.fully_received}<span className="text-sm text-slate-400 font-medium"> / {counts.total_entries}</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">on-time collections</p>
            </CardContent></Card>
          </div>

          {/* Status distribution bar */}
          {counts.total_entries > 0 && (
            <div className="space-y-1">
              <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                <div className="bg-emerald-500" style={{ width: `${(counts.fully_received / counts.total_entries) * 100}%` }} />
                <div className="bg-amber-500" style={{ width: `${(counts.partial / counts.total_entries) * 100}%` }} />
                <div className="bg-red-400" style={{ width: `${(counts.zero_received / counts.total_entries) * 100}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>{counts.fully_received} Full</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>{counts.partial} Partial</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>{counts.zero_received} Zero</span>
              </div>
            </div>
          )}

          {/* Per-entry table */}
          <Card>
            <CardContent className="p-3">
              <p className="text-xs font-bold text-slate-800 mb-2">Entry-Level Breakdown <span className="text-slate-400 font-normal">(sorted by shortfall)</span></p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="closing-report-entries-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
                      <th className="text-left py-1.5 pr-2 font-semibold">Date</th>
                      <th className="text-left py-1.5 pr-2 font-semibold">Project</th>
                      <th className="text-left py-1.5 pr-2 font-semibold">Billing</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">Planned</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">Collected</th>
                      <th className="text-right py-1.5 pr-2 font-semibold">Shortfall</th>
                      <th className="text-center py-1.5 pr-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 && (
                      <tr><td colSpan="7" className="py-6 text-center text-slate-400 text-xs italic">No entries in this week.</td></tr>
                    )}
                    {entries.map((e, i) => {
                      const s = e.status;
                      const StatusIcon = s === "fully_received" ? CheckCircle : s === "partial" ? Circle : AlertTriangle;
                      const iconClr = s === "fully_received" ? "text-emerald-600" : s === "partial" ? "text-amber-600" : "text-red-500";
                      const label = s === "fully_received" ? "Full" : s === "partial" ? "Partial" : "Zero";
                      return (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1.5 pr-2 text-slate-500">{e.date}</td>
                          <td className="py-1.5 pr-2 font-medium text-slate-800">{e.project_name}</td>
                          <td className="py-1.5 pr-2 text-slate-500 text-[10.5px]">{e.billing_type}{e.billing_frequency ? ` · ${e.billing_frequency.replace("_", " ")}` : ""}</td>
                          <td className="py-1.5 pr-2 text-right text-slate-700 font-medium">₹{fmtL(e.planned)}</td>
                          <td className="py-1.5 pr-2 text-right text-emerald-600 font-medium">₹{fmtL(e.collected)}</td>
                          <td className={`py-1.5 pr-2 text-right font-semibold ${e.shortfall > 0 ? "text-amber-600" : "text-slate-300"}`}>{e.shortfall > 0 ? `₹${fmtL(e.shortfall)}` : "—"}</td>
                          <td className="py-1.5 pr-2 text-center">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${iconClr}`}>
                              <StatusIcon className="w-3 h-3" />
                              {label}
                              {e.carried && <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">→CF</span>}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-2 justify-end pt-2 border-t mt-3">
          <Button variant="outline" onClick={onClose} data-testid="close-closing-report-btn">Close</Button>
          <Button className="bg-[#0066CC] hover:bg-[#0052A3] gap-1" onClick={onClose}>
            <TrendingUp className="w-4 h-4" /> View Next Week
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
